// Lógica PURA de la conexión con Escalafy (contrato Economía §5): la nota que sobrevive al viaje al
// OAuth, el `redirect_uri` fijo y el mapeo organización → cliente del asistente.

// El callback es UNA ruta fija de la app, no la pantalla del cliente: el `redirect_uri` se registra
// en Escalafy (registro dinámico, contrato §5.2) y el backend lo exige byte a byte
// (`settings.ESCALAFY_REDIRECT_URIS`; otro valor → 400 `INVALID_REDIRECT_URI`), así que no puede
// llevar el id del cliente. A qué cliente volver viaja en la nota de sessionStorage.
export const ESCALAFY_CALLBACK_PATH = '/escalafy/callback';

const KEY = 'pending_escalafy_connection';
// Mismo vencimiento que el intento OAuth del backend (`EscalafyOAuthAttempt`, 15 min).
const MAX_AGE_MS = 15 * 60 * 1000;

// Quién arrancó el OAuth (contrato «Escalafy como canal» §6). La ruta del callback es UNA para los dos:
// la agencia (desde Economía o desde Conexiones) y el dueño de la tienda desde el portal.
export const ESCALAFY_FLOW_AGENCY = 'agency';
export const ESCALAFY_FLOW_PORTAL = 'portal';

export const AGENCY_CONNECTIONS_PATH = '/agency/conections';
export const PORTAL_CONNECTIONS_PATH = '/portal/connections';

export function escalafyRedirectUri(origin = window.location.origin) {
    return `${origin}${ESCALAFY_CALLBACK_PATH}`;
}

// El parámetro con el que el callback trae de vuelta a la pantalla del cliente para abrir el paso 2.
export const CREDENTIAL_PARAM = 'escalafy_credential';

function idOrNull(value) {
    return value != null && value !== '' ? String(value) : null;
}

/**
 * Anota a dónde volver después del OAuth. Toda entrada al OAuth la ESCRIBE ENTERA (patrón
 * `pendingLinkedinReconnect`): un OAuth abandonado no deja nada que la próxima conexión herede.
 *
 * - Agencia desde Economía: `clientId`, vuelve al paso 2 del asistente de ese cliente.
 * - Agencia desde Conexiones: sin `clientId`, vuelve a Conexiones; `label` es el nombre que se tipeó.
 * - Portal: `flow: 'portal'` + `agencyClientId` (el cliente ACTIVO del portal, destino de la conexión).
 *
 * Si es una reconexión NO viaja acá: la credencial a reconectar la guarda el backend en el intento
 * OAuth, y el callback responde `reconnected`.
 */
export function setPendingEscalafyConnection({clientId, flow = ESCALAFY_FLOW_AGENCY, agencyClientId, label} = {}) {
    try {
        window.sessionStorage.setItem(KEY, JSON.stringify({
            clientId: idOrNull(clientId),
            flow: flow === ESCALAFY_FLOW_PORTAL ? ESCALAFY_FLOW_PORTAL : ESCALAFY_FLOW_AGENCY,
            agencyClientId: idOrNull(agencyClientId),
            label: label ? String(label) : null,
            posted: false,
            at: Date.now(),
        }));
    } catch (error) {
        // Sin sessionStorage el callback no sabe a qué cliente volver y cae al inicio: la conexión
        // igual queda hecha y se retoma desde la pantalla del cliente.
    }
}

/**
 * La nota si sigue vigente, o `null` (vencida se borra). NO se borra al leerla: si el callback se
 * refresca mientras viaja el canje, la nota es lo único que sabe a qué cliente volver (R-ecofe-K02).
 * Una nota anterior a esta tanda, sin `flow`, es de la agencia: la única que existía.
 */
export function readPendingEscalafyConnection(now = Date.now()) {
    let note = null;
    try {
        note = JSON.parse(window.sessionStorage.getItem(KEY));
    } catch (error) {
        note = null;
    }
    if (!note || typeof note.at !== 'number' || now - note.at > MAX_AGE_MS) {
        clearPendingEscalafyConnection();
        return null;
    }
    return {
        clientId: note.clientId || null,
        flow: note.flow === ESCALAFY_FLOW_PORTAL ? ESCALAFY_FLOW_PORTAL : ESCALAFY_FLOW_AGENCY,
        agencyClientId: note.agencyClientId || null,
        label: note.label || null,
        posted: note.posted === true,
    };
}

/**
 * Marca que el canje ya se mandó. El `code` es de un solo uso: un refresh del callback no lo vuelve
 * a postear (perdería el `claim()` del intento y parecería que falló), vuelve al cliente.
 */
export function markPendingEscalafyPosted() {
    try {
        const note = JSON.parse(window.sessionStorage.getItem(KEY));
        if (note) window.sessionStorage.setItem(KEY, JSON.stringify({...note, posted: true}));
    } catch (error) {
        // Sin sessionStorage no hay refresh que proteger: la nota tampoco existía.
    }
}

export function clearPendingEscalafyConnection() {
    try {
        window.sessionStorage.removeItem(KEY);
    } catch (error) {
        // Nada que borrar.
    }
}

/** A dónde vuelve el callback: la pantalla del cliente con la credencial para abrir el paso 2. */
export function escalafyReturnPath(clientId, credentialId) {
    if (!clientId) return '/';
    const query = credentialId ? `?${CREDENTIAL_PARAM}=${encodeURIComponent(credentialId)}` : '';
    return `/client/${clientId}/economia${query}#escalafy`;
}

/**
 * La pantalla de ORIGEN según la nota: el portal, la economía del cliente (con la credencial para el paso
 * 2) o Conexiones. Sin nota (vencida, o sin sessionStorage) no se sabe de dónde vino: el usuario del portal
 * vuelve a sus conexiones y la agencia a Conexiones, donde queda la conexión que se acaba de hacer.
 */
export function escalafyReturnTo(pending, credentialId = null, isPortalUser = false) {
    if (!pending) return isPortalUser ? PORTAL_CONNECTIONS_PATH : AGENCY_CONNECTIONS_PATH;
    if (pending.flow === ESCALAFY_FLOW_PORTAL) return PORTAL_CONNECTIONS_PATH;
    if (pending.clientId) return escalafyReturnPath(pending.clientId, credentialId);
    return AGENCY_CONNECTIONS_PATH;
}

// --- Portal: la elección de la tienda ---------------------------------------------------------------
//
// El callback del portal NO vincula nada: devuelve las organizaciones que ve el token y el dueño elige UNA
// en `/portal/connections`. Esa lista viaja del callback a la pantalla en sessionStorage, con el mismo
// vencimiento que el intento (un F5 en el medio no la pierde; una pestaña nueva, sí).
const PICK_KEY = 'pending_escalafy_portal_pick';

export function setPendingPortalPick({credentialId, agencyClientId, organizations} = {}) {
    try {
        window.sessionStorage.setItem(PICK_KEY, JSON.stringify({
            credentialId: idOrNull(credentialId),
            agencyClientId: idOrNull(agencyClientId),
            organizations: Array.isArray(organizations) ? organizations : [],
            at: Date.now(),
        }));
    } catch (error) {
        // Sin sessionStorage el dueño vuelve al portal sin la lista: reconecta y la ve de nuevo.
    }
}

export function readPendingPortalPick(now = Date.now()) {
    let pick = null;
    try {
        pick = JSON.parse(window.sessionStorage.getItem(PICK_KEY));
    } catch (error) {
        pick = null;
    }
    if (!pick || typeof pick.at !== 'number' || now - pick.at > MAX_AGE_MS || !pick.credentialId) {
        clearPendingPortalPick();
        return null;
    }
    return {
        credentialId: pick.credentialId,
        agencyClientId: pick.agencyClientId || null,
        organizations: Array.isArray(pick.organizations) ? pick.organizations : [],
    };
}

export function clearPendingPortalPick() {
    try {
        window.sessionStorage.removeItem(PICK_KEY);
    } catch (error) {
        // Nada que borrar.
    }
}

// Por qué el dueño no puede compartir una tienda. Un motivo nuevo cae al de «sin datos».
const PORTAL_UNUSABLE_REASONS = new Set(['LIMITED_ROLE', 'PLAN_WITHOUT_API', 'NO_DATA']);

/** La clave del texto de una organización que no se puede compartir (`null` si se puede). */
export function portalUnusableKey(org) {
    if (!org || org.usable === true) return null;
    const reason = PORTAL_UNUSABLE_REASONS.has(org.unusable_reason) ? org.unusable_reason : 'NO_DATA';
    return `portal_connections.escalafy_unusable.${reason}`;
}

/** Con UNA sola tienda que se puede compartir, viene elegida; con varias, elige el dueño. */
export function initialPortalChoice(organizations) {
    const usable = (organizations || []).filter((org) => org && org.usable === true);
    return usable.length === 1 ? String(usable[0].org_id) : null;
}

/** La credencial con la que volvió el callback, o `null`. */
export function credentialFromSearch(search) {
    return new URLSearchParams(search || '').get(CREDENTIAL_PARAM) || null;
}

/**
 * La misma URL sin `?escalafy_credential=`: la pantalla lo consume UNA vez al montar. Pegado en la
 * URL, cada F5 reabría el asistente en el paso 2 con una credencial vieja (R-ecofe-05).
 */
export function pathWithoutCredential({pathname = '', search = '', hash = ''} = {}) {
    const params = new URLSearchParams(search || '');
    params.delete(CREDENTIAL_PARAM);
    const rest = params.toString();
    return `${pathname}${rest ? `?${rest}` : ''}${hash || ''}`;
}

// Errores del callback con salida conocida. El resto, al genérico: nunca el texto del backend.
const CALLBACK_ERROR_KEYS = {
    EXPIRED_STATE: 'economics.escalafy.error.expired',
    CONNECTOR_NOT_CONFIGURED: 'economics.escalafy.error.not_configured',
};

/** La clave del mensaje de un callback fallido. */
export function callbackErrorKey(response) {
    const body = (response && response.raw_body) || {};
    const code = (response && response.error_code) || body.error_code || (response && response.status);
    return CALLBACK_ERROR_KEYS[code] || 'economics.error.generic';
}

/**
 * «Ya se pidió una actualización hace menos de 10 minutos» (429 `SYNC_RATE_LIMITED`): en cuántos
 * MINUTOS se puede volver a pedir, redondeando para arriba. `null` si el backend no lo dijo.
 */
export function retryAfterMinutes(response) {
    const body = (response && response.raw_body) || {};
    const seconds = Number(body.retry_after_seconds);
    if (!Number.isFinite(seconds) || seconds <= 0) return null;
    return Math.max(1, Math.ceil(seconds / 60));
}

/**
 * El mapeo inicial del paso 2: nada elegido, salvo UNA sugerencia — si hay una sola organización
 * usable y sin vincular, se propone para el cliente desde el que se abrió el asistente (es el caso
 * del piloto: un cliente, una tienda). Con varias, el usuario elige: adivinar cuál es cuál por el
 * nombre sería mapear la plata de una tienda a otra.
 */
export function initialMapping(organizations, clientId) {
    const usable = (organizations || []).filter((org) => org && org.usable === true && !org.linked_to);
    if (usable.length === 1 && clientId) return {[usable[0].org_id]: String(clientId)};
    return {};
}

/**
 * El mapeo al RECARGAR las organizaciones (después de un vínculo parcial): lo que el usuario ya había
 * elegido para las que siguen libres se conserva; las que se vincularon salen solas.
 */
export function mergeMapping(organizations, previous, clientId) {
    const free = new Set((organizations || [])
        .filter((org) => org && org.usable === true && !org.linked_to).map((org) => String(org.org_id)));
    const kept = Object.entries(previous || {})
        .filter(([orgId, value]) => value && free.has(String(orgId)))
        .reduce((acc, [orgId, value]) => ({...acc, [orgId]: value}), {});
    return {...initialMapping(organizations, clientId), ...kept};
}

/** Las organizaciones que se van a vincular: usables, sin vincular y con un cliente elegido. */
export function selectedLinks(organizations, mapping) {
    return (organizations || [])
        .filter((org) => org && org.usable === true && !org.linked_to && mapping[org.org_id])
        .map((org) => ({orgId: org.org_id, orgName: org.name, agencyClientId: mapping[org.org_id]}));
}

/** ¿Alguna organización necesita el aviso del rol (Media buyer)? */
export function needsRoleCallout(organizations) {
    return (organizations || []).some((org) => org && org.unusable_reason === 'LIMITED_ROLE');
}

/** Iniciales para el avatar de la organización («Tienda Demo» → «TD»). */
export function orgInitials(name) {
    const words = String(name || '').trim().split(/\s+/).filter((word) => /[\p{L}\p{N}]/u.test(word));
    if (!words.length) return '?';
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
}
